import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Mail, Lock, Shield, Zap } from 'lucide-react';
import { login } from '../../store/slices/authSlice';
import { RootState } from '../../store';

import toast from 'react-hot-toast';
import axios from 'axios';
import { Modal } from '../ui/Modal';
import DiasporawiseLogo from '../../assets/DWBRANDblue.png';
import { API_BASE_URL } from '../../config/api';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const forgotPasswordValidationSchema = yup.object({
  email: yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

type LoginFormData = yup.InferType<typeof schema>;
type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordValidationSchema>;

const LoginForm: React.FC = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is already authenticated and redirect
  useEffect(() => {
    if (token) {
      // Admins should go to admin panel
      navigate('/admin', { replace: true });
    }
  }, [token, user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const {
    register: registerForgotPassword,
    handleSubmit: handleSubmitForgotPassword,
    formState: { errors: forgotPasswordErrors, isSubmitting: isForgotPasswordSubmitting },
    reset: resetForgotPassword,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordValidationSchema),
  });

  const onSubmitForgotPassword = async (data: ForgotPasswordFormData) => {
    await handleForgotPassword(data, { setSubmitting: () => { } });
  };

  const handleLogin = async (values: { email: string; password: string }, { setSubmitting }: any) => {
    setIsLoading(true);
    try {
      const baseUrl = API_BASE_URL;
      console.log('Base URL:', baseUrl);
      const endpoint = "/admin/login";

      const response = await axios.post(`${baseUrl}${endpoint}`, {
        email: values.email,
        password: values.password,
      });

      if (
        response.status === 400 ||
        response.status === 404 ||
        response.status === 401 ||
        response.status === 429
      ) {
        const message = response?.data?.message || "Login failed"
        toast.error(message);
        return;
      } else {
        if (response.data?.success === true && response.data?.status === 200) {
          console.log('Full login response:', response.data);

          // Extract token from response - try different possible structures
          let token = '';
          let userData: any = {};

          if (response.data.data) {
            // If data is a string (token)
            if (typeof response.data.data === 'string') {
              token = response.data.data;
            }
            // If data is an object with token property
            else if (response.data.data.token) {
              token = response.data.data.token;
              userData = response.data.data.user || response.data.data;
            }
            // If data is an object with jwt_token property
            else if (response.data.data.jwt_token) {
              token = response.data.data.jwt_token;
              userData = response.data.data.user || response.data.data;
            }
            // If data is an object, try to find token in nested structure
            else if (response.data.data.data && response.data.data.data.token) {
              token = response.data.data.data.token;
              userData = response.data.data.data.user || response.data.data.data;
            }
          }

          // Fallback: check if token is directly in response
          if (!token && response.data.token) {
            token = response.data.token;
          }

          console.log('Extracted token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN FOUND');
          console.log('User data:', userData);

          if (!token) {
            toast.error('Login successful but no authentication token received. Please contact support.');
            return;
          }

          const userRole = userData.role || response?.data?.data?.user?.role || 'admin';
          console.log('Role parsed:', userRole);

          dispatch(login({
            user: {
              email: values.email,
              role: userRole,
              name: userData.name || response?.data?.data?.name || values.email
            },
            token: token
          }));
          toast.success(`Login successful! Welcome as ${userRole === 'admin' ? 'Admin' : userRole === 'manager' ? 'Manager' : 'Exclusive'}`);
        } else {
          toast.error(response?.data?.message || "Login failed");
        }
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Login failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
      if (setSubmitting) {
        setSubmitting(false);
      }
    }
  };

  const handleForgotPassword = async (values: { email: string }, { setSubmitting }: any) => {
    try {
      const baseUrl = API_BASE_URL;
      const endpoint = "/admin/forgot-password";
      const response = await axios.post(`${baseUrl}${endpoint}`, {
        email: values.email,
      });

      if (response.data?.success === true && response.data?.status === 200) {
        toast.success('Password reset link sent to your email!');
        resetForgotPassword();
        setShowForgotPassword(false);
      } else {
        toast.error(response?.data?.message || "Failed to send reset link");
      }
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      {/* Main Content */}
      <div className="w-full max-w-md">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* Back Button */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => {
                // Check if we can go back in browser history
                if (window.history.length > 1 && document.referrer) {
                  // Try to go back in history
                  navigate(-1);
                } else {
                  // Fallback to home page
                  navigate('/', { replace: true });
                }
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="flex-1"></div>
          </div>

          <Link to="/" className="inline-block mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-3 hover:scale-105 transition-transform duration-300 shadow-md mx-auto">
              <img
                src={DiasporawiseLogo}
                alt="Diasporawise Logo"
                className="h-full w-full object-contain rounded-full"
              />
            </div>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Login Form */}
          <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50"
                  placeholder="super.admin@gmail.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Forgot Password?
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          resetForgotPassword();
        }}
        title="Reset Password"
        size="md"
      >
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Forgot your password?</h3>
            <p className="text-gray-600 text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmitForgotPassword(onSubmitForgotPassword)} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  {...registerForgotPassword('email')}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
              {forgotPasswordErrors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {forgotPasswordErrors.email.message}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  resetForgotPassword();
                }}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
              <button
                type="submit"
                disabled={isForgotPasswordSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                {isForgotPasswordSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default LoginForm;